import { createElement, useMemo } from 'react';
import type { ReactElement } from 'react';
import { useAppSelector } from '../redux/hooks';
import { selectedStudentSkillStates } from '../redux/reducers/studentSkillsSlice';
import { selectedUnit } from '../redux/reducers/unitSlice';
import { selectedStudentSkillStateAfterTakeAssessment } from '../redux/reducers/studentSkillAfterTakeAssessmentSlice';
import { applyOptimisticValues } from '../businessLogicService/StudentDashboardAux/applyOptimisticValues';
import {
  resolveUnitBaseColor,
  unitFoundationsCardColors,
  type UnitFoundationsCardColors,
} from '../businessLogicService/StudentDashboardAux/getColorByUnit';
import { skillSelector, sortFoundationsSkills } from '../businessLogicService/StudentDashboardAux/foundationSkills';
import {
  areFoundationsAllPassed,
  chunkIntoRows,
  consolidatePaths,
  firstRowSize,
  foundationsLastRowSize,
  hasScore,
  isPassed,
  lastRowSize,
  type PathSkillNode,
} from '../businessLogicService/StudentDashboardAux/pathToSkills';
import type { FoundationSkills } from '../components/Dashboard/DashboardContainerBody';
import type { StudentSkillStates } from '../models/StudentSkillState';
import type { DashboardConnectorType } from '../models/StudentDashboard';
import type { DashboardPracticeProblemsActionsQuery$data } from '../graphQLOperations/__generated__/DashboardPracticeProblemsActionsQuery.graphql';
import { TargetSkillTypeLabel } from '../models/TargetSkillType';
import DashboardCard from '../components/Dashboard/DashboardCard';
import { ShortConnector } from '../components/Dashboard/DashboardConnectors/ShortConnector';
import { LongConnector } from '../components/Dashboard/DashboardConnectors/LongConnector';
import { MiddleConnector } from '../components/Dashboard/DashboardConnectors/MiddleConnector';
import { VerticalConnector } from '../components/Dashboard/DashboardConnectors/VerticalConnector';
import { ShortPathConnector } from '../components/Dashboard/DashboardConnectors/ShortPathConnector';
import { LongPathConnector } from '../components/Dashboard/DashboardConnectors/LongPathConnector';
import { PathSkillBadge } from '../components/Dashboard/DashboardConnectors/PathSkillBadge';
import { FoundationsMilestoneBadge } from '../components/Dashboard/DashboardConnectors/FoundationsMilestoneBadge';
import { TargetMilestoneBadge } from '../components/Dashboard/DashboardConnectors/TargetMilestoneBadge';
import { PathEndNode } from '../components/Dashboard/DashboardConnectors/PathEndNode';

export interface UseDashboardUnitPathParams {
  paths: PathSkillNode[][][] | undefined;
  foundationSkills: FoundationSkills[];
  // Practice-problems sessions for the student; each card whose skill appears here is flagged as
  // currently being practised via the DashboardCard `isPracticing` prop.
  lessonPractice: DashboardPracticeProblemsActionsQuery$data['learnosityPracticeProblemsSessions'];
}

export interface UseDashboardUnitPathResult {
  unitPath: ReactElement;
  // Global index of the "Back to position" target connector (first unlocked, else last passed), or
  // null when there are no connectors. The element carrying it is tagged with UNIT_PATH_POSITION_ANCHOR_ID.
  positionKey: number | null;
  // True when the path has a first unlocked connector (the current frontier). That connector always
  // coincides with positionKey, so the "Towards" marker anchors to UNIT_PATH_POSITION_ANCHOR_ID too.
  hasUnlockedFrontier: boolean;
  // True once at least one connector has been passed. False lets "Back to position" fall back to
  // scrolling to the very top of the page instead of to a specific connector.
  hasPassedConnector: boolean;
}

// DOM id placed on the "Back to position" target connector so the control can locate it.
export const UNIT_PATH_POSITION_ANCHOR_ID = 'unit-path-position-anchor';

// Each segment of the path (the Foundations panel, then one per target path) is tagged with these
// data attributes so a sibling scroll-spy (useActiveUnitPathSegment) can read the segment order and
// labels straight from the DOM. Attributes, not `id`s, because there is one per segment and the spy
// selects them as a set in document order. On a target path the tagged element is the connector's
// "Path to target N" badge — the thing that visually announces the segment — not the connector
// wrapper, which carries UNIT_PATH_POSITION_ANCHOR_ID instead.
export const UNIT_PATH_SEGMENT_ATTR = 'data-unit-path-segment';
export const UNIT_PATH_SEGMENT_LABEL_ATTR = 'data-unit-path-segment-label';
// Per-segment progress (completed / total skills in that segment), so the header can show a progress
// bar for the segment currently in view without needing the segmented skill data itself.
export const UNIT_PATH_SEGMENT_COMPLETED_ATTR = 'data-unit-path-segment-completed';
export const UNIT_PATH_SEGMENT_TOTAL_ATTR = 'data-unit-path-segment-total';
// Human name of the segment's target/checkpoint skill (e.g. "Equation"), shown next to the ordinal in
// the header. Empty for the Foundations segment.
export const UNIT_PATH_SEGMENT_NAME_ATTR = 'data-unit-path-segment-name';
// Whether the unlocked frontier has reached this segment, i.e. the connector leading into it is not
// locked. Since the progression unlocks in document order, this is monotonic: every segment up to the
// one holding the last non-locked connector is reached. Lets the rail's segment strip take the unit
// accent only while the student is scrolled over the open part of the path, and read neutral beyond it.
export const UNIT_PATH_SEGMENT_REACHED_ATTR = 'data-unit-path-segment-reached';

export const FOUNDATIONS_SEGMENT_KEY = 'foundations';
export const FOUNDATIONS_SEGMENT_LABEL = 'Foundations';

// `ordinal` is the connector's 1-based badge index (the same `badgeIndex` passed to renderConnector),
// so the segment key/label always match the "Path to target N" badge drawn on that connector.
export const unitPathSegmentKey = (ordinal: number): string => `path-${ordinal}`;
// Short header label for a target path. The in-path connector badge still reads "Path to target N"
// (PathSkillBadge); the header uses this compact form.
export const unitPathSegmentLabel = (ordinal: number): string => `Target ${ordinal}`;

// Completed-skill count for a segment. `isPassed` is the same completion rule the header's badges use
// (mapSkillStatus → Passed / Placed Out), so the counts stay consistent across the dashboard.
const passedCount = (skills: StudentSkillStates[]): number => skills.filter(isPassed).length;

// Human name of a path's target/checkpoint skill (the TARGET-type skill in that path), e.g. "Equation".
const targetSkillName = (skills: StudentSkillStates[]): string =>
  skills.find(skill => skill.targetSkillType === TargetSkillTypeLabel.TARGET)?.skill.name ?? '';

const targetMilestoneType = (
  targetPath: StudentSkillStates[][],
  joinType?: DashboardConnectorType,
): DashboardConnectorType => joinType ?? (targetPath[targetPath.length - 1].every(isPassed) ? 'passed' : 'locked');

// Within the foundations, connectors still unlock in fixed blocks of ConnectorsLevelFactor (top to
// bottom): the first block is open and every later block stays locked until the current block is all
// passed. The path region unlocks level by level instead; both are stitched into one continuous
// progression by computeUnitPathState.
const ConnectorsLevelFactor = 4;

const PATH_ELBOW_HEIGHT = 111;

type SegmentSpanKind = 'foundations' | 'target';

interface SegmentSpan {
  wrapperClassName: string;
  elbowTopClassName: string;
  straightScaleClassName: string;
  elbowHead: number;
  elbowTail: number;
  milestoneTopClassName: string;
  badgeTopClassName: string;
}

// Tailwind keeps only classes it can read as whole literals, so each length is spelled out per span.
const SEGMENT_SPANS: Record<SegmentSpanKind, SegmentSpan> = {
  foundations: {
    wrapperClassName: 'tw-z-10 tw-mt-[-128px] tw-h-[416px]',
    elbowTopClassName: 'tw-top-[141px]',
    straightScaleClassName: 'tw-scale-y-[2.4471]',
    elbowHead: 141,
    elbowTail: 416 - 141 - PATH_ELBOW_HEIGHT,
    milestoneTopClassName: 'tw-top-[11px]',
    badgeTopClassName: 'tw-top-[273px]',
  },
  target: {
    wrapperClassName: 'tw-h-[440px]',
    elbowTopClassName: 'tw-top-[164px]',
    straightScaleClassName: 'tw-scale-y-[2.5882]',
    elbowHead: 164,
    elbowTail: 440 - 164 - PATH_ELBOW_HEIGHT,
    milestoneTopClassName: 'tw-top-[20px]',
    badgeTopClassName: 'tw-top-[298px]',
  },
};

// The unit path is one linear sequence of cards C[0..N-1] (foundations then paths, in render order),
// where connector `c` joins C[c] and C[c+1]. The cursor hands each rendered card/connector its global
// index so its state can be looked up from the single progression below.
interface UnitPathCursor {
  card: number;
  connector: number;
}

interface UnitPathState {
  connectorType: (connectorIndex: number) => DashboardConnectorType;
  isCardBlocked: (cardIndex: number) => boolean;
  // Index of the "Back to position" target connector: the first unlocked connector, else the last
  // passed one, else null when there are no connectors.
  positionConnectorIndex: number | null;
  // Index of the first unlocked connector (the current frontier), or null when nothing is unlocked.
  // When present it always equals positionConnectorIndex.
  firstUnlockedConnectorIndex: number | null;
  // Whether at least one connector in the whole progression has already been passed.
  hasPassedConnector: boolean;
}

/**
 * Resolves the whole unit path as ONE continuous progression, measured from the first foundation:
 *  - `passed`: a connector whose both endpoints are inside the leading run of completed skills.
 *  - `unlocked`: everything up to the current frontier — foundations advance in blocks of
 *    ConnectorsLevelFactor, paths advance one level at a time (only once the foundations are complete),
 *    and the reach is extended to any out-of-order completed skill.
 *  - `locked`: beyond that reach.
 * A card is blocked when the connector leading into it is locked, unless it already has a score.
 */
const computeUnitPathState = (
  foundationCards: StudentSkillStates[],
  consolidated: StudentSkillStates[][][],
  foundationsComplete: boolean,
): UnitPathState => {
  const foundationCount = foundationCards.length;
  const allCards = [...foundationCards, ...consolidated.flat(2)];
  const cardCount = allCards.length;

  // Leading run of passed skills, continuous from the first foundation.
  let passedPrefix = 0;
  while (passedPrefix < cardCount && isPassed(allCards[passedPrefix])) {
    passedPrefix += 1;
  }
  const passedConnectorCount = Math.max(0, passedPrefix - 1);

  // Out-of-order reach: the last completed skill anywhere in the sequence.
  let lastPassedIndex = cardCount - 1;
  while (lastPassedIndex >= 0 && !isPassed(allCards[lastPassedIndex])) {
    lastPassedIndex -= 1;
  }

  // Foundation frontier: fixed blocks of ConnectorsLevelFactor, capped at the last internal foundation
  // connector so the bridge (connector foundationCount - 1) only unlocks via the path frontier once
  // the foundations are complete.
  const foundationPassedConnectors = Math.max(0, Math.min(passedPrefix, foundationCount) - 1);
  const activeBlockEnd = (Math.floor(foundationPassedConnectors / ConnectorsLevelFactor) + 1) * ConnectorsLevelFactor;
  const foundationBlockBoundary = foundationCount > 0 ? Math.min(activeBlockEnd, foundationCount - 1) : 0;

  // Path frontier: level by level, active only once the foundations are complete. The active (first
  // not-fully-passed) level and everything before it is unlocked; the connector into the next level
  // stays locked.
  let pathLevelBoundary = 0;
  if (foundationsComplete) {
    const levels = consolidated.flat();
    let passedLevelRun = 0;
    while (passedLevelRun < levels.length && levels[passedLevelRun].every(isPassed)) {
      passedLevelRun += 1;
    }
    if (passedLevelRun >= levels.length) {
      pathLevelBoundary = cardCount - 1;
    } else {
      let activeLevelStart = foundationCount;
      for (let level = 0; level < passedLevelRun; level += 1) {
        activeLevelStart += levels[level].length;
      }
      pathLevelBoundary = activeLevelStart + levels[passedLevelRun].length - 1;
    }
  }

  const unlockedReachEnd = Math.max(foundationBlockBoundary, pathLevelBoundary, lastPassedIndex);

  // The "Back to position" target: the first unlocked connector, or the last passed connector when no
  // connector is unlocked (e.g. the whole unit is complete), or null when there are no connectors.
  const positionConnectorIndex =
    passedConnectorCount < unlockedReachEnd
      ? passedConnectorCount
      : passedConnectorCount > 0
      ? passedConnectorCount - 1
      : null;

  // The first unlocked connector is the frontier when the reach extends past the passed run.
  const firstUnlockedConnectorIndex = passedConnectorCount < unlockedReachEnd ? passedConnectorCount : null;

  return {
    connectorType: (connectorIndex: number): DashboardConnectorType => {
      if (connectorIndex < passedConnectorCount) {
        return 'passed';
      }
      return connectorIndex < unlockedReachEnd ? 'unlocked' : 'locked';
    },
    isCardBlocked: (cardIndex: number): boolean => cardIndex > unlockedReachEnd && !hasScore(allCards[cardIndex]),
    positionConnectorIndex,
    firstUnlockedConnectorIndex,
    hasPassedConnector: passedConnectorCount > 0,
  };
};

// The DOM anchor id for the connector at `connectorIndex` when it is the current position (last passed
// connector), else undefined. Used to tag the one connector the "Back to position" control targets.
// Before anything is passed the anchor moves to the first card instead (see cardAnchorIdFor), so this
// never tags a connector in that case.
const anchorIdFor = (connectorIndex: number | null, state: UnitPathState): string | undefined =>
  state.hasPassedConnector && connectorIndex !== null && connectorIndex === state.positionConnectorIndex
    ? UNIT_PATH_POSITION_ANCHOR_ID
    : undefined;

// The DOM anchor id for the card at `cardIndex`, when no connector has been passed yet — in that case
// the current position is the very first card (index 0) rather than a connector.
const cardAnchorIdFor = (cardIndex: number, state: UnitPathState): string | undefined =>
  !state.hasPassedConnector && cardIndex === 0 ? UNIT_PATH_POSITION_ANCHOR_ID : undefined;

// Attributes identifying the unit-path segment an element starts. Spread onto the element that marks
// that start — see segmentAnchorProps in renderConnector.
type SegmentAnchorProps = Record<string, string | number | undefined>;

// Distance from a span's center line to each of its vertical segments: the upper one (leaving the
// previous segment) runs down the elbow's right edge and the lower one (entering the next path) down its
// left edge, so each marker riding a segment is offset by half the elbow's width. The straight 1→1
// span has a single centered segment, so both markers stay on the center line.
const spanSegmentOffsets = (isLong: boolean, isVertical: boolean): { upper: string; lower: string } =>
  isVertical
    ? { upper: 'tw-ml-0', lower: 'tw-ml-0' }
    : isLong
    ? { upper: 'tw-ml-[255px]', lower: 'tw-ml-[-255px]' }
    : { upper: 'tw-ml-[127.5px]', lower: 'tw-ml-[-127.5px]' };

// A marker pinned `topClassName` down a span and centered on one of its vertical segments.
const spanOverlay = (
  key: string,
  topClassName: string,
  offsetClassName: string,
  marker: ReactElement,
  segmentProps: SegmentAnchorProps = {},
): ReactElement =>
  createElement(
    'div',
    {
      key,
      className: `tw-absolute tw-left-1/2 ${topClassName} ${offsetClassName} tw-z-10 tw-translate-x-[-50%]`,
      ...segmentProps,
    },
    marker,
  );

interface ConnectorOptions {
  key: string;
  fromRowSize: number;
  toRowSize: number;
  type: DashboardConnectorType;
  // 'row' uses the plain connectors (between rows of the same column); 'path' uses the path variants
  // (between target paths / from foundations into the first path) and is `tw-relative` so a badge can
  // sit over it.
  variant: 'row' | 'path';
  // When set on a span, a PathSkillBadge carrying this 1-based ordinal rides the segment entering the
  // next path.
  badgeIndex?: number;
  // A join between segments: owns its span as layout height, with the milestone on the segment leaving
  // the previous one and the badge on the segment entering the next.
  span?: SegmentSpanKind;
  milestone?: ReactElement;
  // When set, tags the wrapper with this DOM id (used to anchor the "Back to position" control).
  wrapperId?: string;
  // When set, tags the wrapper as a unit-path segment anchor (for the header scroll-spy). Uses data
  // attributes so it can coexist with wrapperId on the same connector.
  segmentKey?: string;
  segmentLabel?: string;
  // Progress of the segment this anchor introduces (completed / total skills), stamped alongside.
  segmentCompleted?: number;
  segmentTotal?: number;
  // Name of the segment's target/checkpoint skill (e.g. "Equation").
  segmentName?: string;
}

/**
 * The single connector renderer shared by every join in the unit path — between foundation rows,
 * between path rows, between target paths, and the foundations→first-path bridge. The row size on
 * each side picks the shape (mirrored across all callers):
 *  - 2 → 2: Long connector,
 *  - 1 → 1: Vertical connector,
 *  - 1 → 2 / 2 → 1: Short connector, shifted to sit its stub on the single card's center.
 */
const renderConnector = ({
  key,
  fromRowSize,
  toRowSize,
  type,
  variant,
  badgeIndex,
  span: spanKind,
  milestone,
  wrapperId,
  segmentKey,
  segmentLabel,
  segmentCompleted,
  segmentTotal,
  segmentName,
}: ConnectorOptions): ReactElement => {
  const isLongConnector = fromRowSize === 2 && toRowSize === 2;
  const isVerticalConnector = fromRowSize === 1 && toRowSize === 1;
  const span = spanKind ? SEGMENT_SPANS[spanKind] : null;
  const shiftClassName =
    isLongConnector || isVerticalConnector
      ? ''
      : fromRowSize === 1
      ? 'tw-translate-x-[-127.5px]'
      : 'tw-translate-x-[127.5px]';

  const wrapperClassName = [
    variant === 'path' ? 'tw-relative' : null,
    'tw-flex',
    'tw-justify-center',
    shiftClassName,
    // A span owns its full height as real layout height, so the path below simply flows into place;
    // everything inside is positioned against this box. The foundations span also pulls its top up by
    // the panel's bottom padding so it starts on the last foundation card's border, above the panel.
    span?.wrapperClassName ?? null,
  ]
    .filter(Boolean)
    .join(' ');

  const ConnectorComponent =
    variant === 'path'
      ? isLongConnector
        ? LongPathConnector
        : isVerticalConnector
        ? VerticalConnector
        : ShortPathConnector
      : isLongConnector
      ? LongConnector
      : isVerticalConnector
      ? VerticalConnector
      : ShortConnector;

  // A span's connector is positioned against the span's box rather than stacked in flow — and in its
  // own element, so the markers beside it are never distorted by the straight connector's vertical
  // stretch. The Short/Long path elbows keep their shape and grow their two vertical segments instead
  // (verticalHead up to the card border above, verticalTail down to the card border below).
  const connectorElement = span
    ? createElement(
        'div',
        {
          key: 'connector',
          className: isVerticalConnector
            ? `tw-absolute tw-left-1/2 tw-top-0 tw-origin-top tw-translate-x-[-50%] ${span.straightScaleClassName}`
            : `tw-absolute tw-left-1/2 ${span.elbowTopClassName} tw-translate-x-[-50%]`,
        },
        isVerticalConnector
          ? createElement(VerticalConnector, { type })
          : createElement(isLongConnector ? LongPathConnector : ShortPathConnector, {
              type,
              verticalHead: span.elbowHead,
              verticalTail: span.elbowTail,
            }),
      )
    : createElement(ConnectorComponent, { key: 'connector', type });

  // The segment anchor rides the connector's "Path to target N" badge, not the wrapper. The badge is
  // what visually announces the segment, so the header and rail flip to "Target N" exactly as it slides
  // under the pinned header, and the header's segment stepper lands the student on it. On a span the
  // two are ~300px apart, which is the whole difference. The wrapper keeps `wrapperId` — the "Back to
  // position" target — which is a different question with a different answer.
  const segmentAnchorProps: SegmentAnchorProps = {
    [UNIT_PATH_SEGMENT_ATTR]: segmentKey,
    [UNIT_PATH_SEGMENT_LABEL_ATTR]: segmentLabel,
    [UNIT_PATH_SEGMENT_COMPLETED_ATTR]: segmentCompleted,
    [UNIT_PATH_SEGMENT_TOTAL_ATTR]: segmentTotal,
    [UNIT_PATH_SEGMENT_NAME_ATTR]: segmentName,
    // This connector IS the entry to the segment it announces, so its own state answers whether the
    // frontier reached it. Omitted on connectors that carry no segment anchor.
    [UNIT_PATH_SEGMENT_REACHED_ATTR]: segmentKey ? String(type !== 'locked') : undefined,
  };
  const hasBadge = badgeIndex !== undefined;

  const children: ReactElement[] = [connectorElement];
  if (span) {
    const offsets = spanSegmentOffsets(isLongConnector, isVerticalConnector);
    if (milestone) {
      children.push(spanOverlay('milestone', span.milestoneTopClassName, offsets.upper, milestone));
    }
    if (badgeIndex !== undefined) {
      children.push(
        spanOverlay(
          'badge',
          span.badgeTopClassName,
          offsets.lower,
          createElement(PathSkillBadge, { type, index: badgeIndex }),
          segmentAnchorProps,
        ),
      );
    }
  }

  return createElement(
    'div',
    {
      key,
      id: wrapperId,
      // Fallback only: every connector that announces a segment also draws a badge, which is the better
      // anchor. This keeps the anchor reachable if one ever doesn't.
      ...(hasBadge ? {} : segmentAnchorProps),
      className: wrapperClassName,
    },
    children,
  );
};

// A row of one or two cards. Two cards are joined horizontally by a MiddleConnector scaled to span
// the 10px gaps on either side, keeping the vertical short/long connectors aligned. When a row has a
// single card the connector type is ignored.
const renderCardRow = (
  key: string,
  cards: ReactElement[],
  middleConnectorType: DashboardConnectorType | null,
  middleConnectorId?: string,
): ReactElement => {
  const rowChildren =
    cards.length === 2 && middleConnectorType !== null
      ? [
          cards[0],
          createElement(
            'div',
            { key: 'middle-connector', id: middleConnectorId, className: 'tw-inline-flex tw-scale-x-[1.1176]' },
            createElement(MiddleConnector, { type: middleConnectorType }),
          ),
          cards[1],
        ]
      : cards;

  return createElement('div', { key, className: 'tw-flex tw-items-center tw-gap-[10px]' }, rowChildren);
};

// The foundation cards column: one card first, then the rest in pairs. Cards and connectors take their
// state from the shared progression via the running cursor.
const renderFoundationCards = (
  items: StudentSkillStates[],
  state: UnitPathState,
  cursor: UnitPathCursor,
  practicingSkillIds: Set<string>,
): ReactElement | null => {
  if (items.length === 0) {
    return null;
  }

  const groups: StudentSkillStates[][] = [[items[0]]];
  for (let index = 1; index < items.length; index += 2) {
    groups.push(items.slice(index, index + 2));
  }

  const elements: ReactElement[] = [];

  groups.forEach((group, groupIndex) => {
    const cards = group.map(skill => {
      const cardIndex = cursor.card++;
      return createElement(DashboardCard, {
        key: skill.id,
        skillsConsolidated: skill,
        isBlocked: state.isCardBlocked(cardIndex),
        id: cardAnchorIdFor(cardIndex, state),
        hasPassedConnector: state.hasPassedConnector,
        isPracticing: practicingSkillIds.has(skill.skill.id),
      });
    });

    // The middle connector is consumed before the between-group connector, keeping the cursor in step
    // with the connectors' creation order (which matches their global index).
    const middleConnectorIndex = group.length === 2 ? cursor.connector++ : null;
    elements.push(
      renderCardRow(
        `group-${groupIndex}`,
        cards,
        middleConnectorIndex !== null ? state.connectorType(middleConnectorIndex) : null,
        anchorIdFor(middleConnectorIndex, state),
      ),
    );

    const nextGroup = groups[groupIndex + 1];
    if (nextGroup) {
      const connectorIndex = cursor.connector++;
      elements.push(
        renderConnector({
          key: `connector-${groupIndex}`,
          fromRowSize: group.length,
          toRowSize: nextGroup.length,
          type: state.connectorType(connectorIndex),
          variant: 'row',
          wrapperId: anchorIdFor(connectorIndex, state),
        }),
      );
    }
  });

  return createElement('div', { className: 'tw-flex tw-flex-col tw-items-center' }, elements);
};

// The full foundations panel: the pink rounded card carrying the unit background color, the
// "Start on Foundations" badge (unit fill/stroke), and the foundation cards column. It anchors the
// Foundations segment, always flagged as reached: the progression starts here, so it is inside the
// unlocked part of the path no matter how little the student has done.
const renderFoundations = (
  skillCards: StudentSkillStates[],
  unitColors: UnitFoundationsCardColors,
  state: UnitPathState,
  cursor: UnitPathCursor,
  practicingSkillIds: Set<string>,
): ReactElement => (
  <div
    data-unit-path-segment={FOUNDATIONS_SEGMENT_KEY}
    data-unit-path-segment-label={FOUNDATIONS_SEGMENT_LABEL}
    data-unit-path-segment-completed={passedCount(skillCards)}
    data-unit-path-segment-total={skillCards.length}
    data-unit-path-segment-reached="true"
    className={`paper-grid-overlay flex gap-lg tw-w-full ${unitColors.background} tw-rounded-b-[10rem] !tw-rounded-t-xl tw-relative tw-self-stretch tw-p-6 !tw-pb-[8rem] tw-inline-flex tw-flex-col tw-justify-start tw-items-center`}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="229" height="85" viewBox="0 0 229 85" fill="none">
      <path
        d="M114.062 2C146.277 2 191.165 11.5494 215.801 17.4492C224.002 19.4136 228.339 28.2797 224.986 36.1016L208.714 74.0693C205.61 81.3109 197.022 84.7574 189.427 82.0732C172.67 76.1511 144.158 68 114.062 68C83.967 68 55.4547 76.1511 38.6982 82.0732C31.1033 84.7574 22.5147 81.3109 19.4111 74.0693L3.13867 36.1016C-0.213577 28.2797 4.12281 19.4136 12.3242 17.4492C36.9603 11.5494 81.8483 2 114.062 2Z"
        className={`${unitColors.svgFill} ${unitColors.svgStroke}`}
        strokeWidth="4"
      />

      <text
        x="114"
        y="32"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontSize="14"
        fontWeight="bold"
        fill="#ffffffc2"
      >
        <tspan x="114" dy="0">
          Starts on
        </tspan>
        <tspan x="114" dy="16">
          Foundations
        </tspan>
      </text>
    </svg>

    {renderFoundationCards(skillCards, state, cursor, practicingSkillIds)}
  </div>
);

// One target's path from its levels: each level is chunked into rows of at most two cards. Cards and
// connectors take their state from the shared progression via the running cursor.
const renderPath = (
  levels: StudentSkillStates[][],
  pathIndex: number,
  state: UnitPathState,
  cursor: UnitPathCursor,
  practicingSkillIds: Set<string>,
  // Segment anchor stamped on the path container — only used for the first path when the
  // foundations→first-path bridge (which normally carries the "Path to target 1" anchor) is absent.
  segmentKey?: string,
  segmentLabel?: string,
): ReactElement | null => {
  if (levels.length === 0) {
    return null;
  }

  const rows = levels.flatMap(level => chunkIntoRows(level));

  const elements: ReactElement[] = [];

  rows.forEach((rowSkills, rowIndex) => {
    const cards = rowSkills.map(skill => {
      const cardIndex = cursor.card++;
      return createElement(DashboardCard, {
        key: skill.id,
        skillsConsolidated: skill,
        isBlocked: state.isCardBlocked(cardIndex),
        id: cardAnchorIdFor(cardIndex, state),
        hasPassedConnector: state.hasPassedConnector,
        isPracticing: practicingSkillIds.has(skill.skill.id),
      });
    });

    const middleConnectorIndex = rowSkills.length === 2 ? cursor.connector++ : null;
    elements.push(
      renderCardRow(
        `row-${rowIndex}`,
        cards,
        middleConnectorIndex !== null ? state.connectorType(middleConnectorIndex) : null,
        anchorIdFor(middleConnectorIndex, state),
      ),
    );

    const nextRow = rows[rowIndex + 1];
    if (nextRow) {
      const connectorIndex = cursor.connector++;
      elements.push(
        renderConnector({
          key: `connector-${rowIndex}`,
          fromRowSize: rowSkills.length,
          toRowSize: nextRow.length,
          type: state.connectorType(connectorIndex),
          variant: 'row',
          wrapperId: anchorIdFor(connectorIndex, state),
        }),
      );
    }
  });

  const pathSkills = levels.flat();
  return createElement(
    'div',
    {
      key: `path-${pathIndex}`,
      [UNIT_PATH_SEGMENT_ATTR]: segmentKey,
      [UNIT_PATH_SEGMENT_LABEL_ATTR]: segmentLabel,
      [UNIT_PATH_SEGMENT_COMPLETED_ATTR]: segmentKey ? passedCount(pathSkills) : undefined,
      [UNIT_PATH_SEGMENT_TOTAL_ATTR]: segmentKey ? pathSkills.length : undefined,
      [UNIT_PATH_SEGMENT_NAME_ATTR]: segmentKey ? targetSkillName(pathSkills) : undefined,
      // Only the first path anchors itself, and only when there is no bridge (no foundations) — it is
      // then the start of the progression, so it is always inside the unlocked part.
      [UNIT_PATH_SEGMENT_REACHED_ATTR]: segmentKey ? 'true' : undefined,
      className: 'tw-flex tw-flex-col tw-items-center',
    },
    elements,
  );
};

const renderPathEnd = (levels: StudentSkillStates[][], ordinal: number): ReactElement => {
  const type = targetMilestoneType(levels);
  return createElement(
    'div',
    {
      key: 'path-end',
      className: `tw-relative tw-h-[226px] tw-w-[15px] ${lastRowSize(levels) === 2 ? 'tw-translate-x-[255px]' : ''}`,
    },
    createElement(
      'div',
      { key: 'line', className: 'tw-absolute tw-left-0 tw-top-0 tw-origin-top tw-scale-y-[1.2176]' },
      createElement(VerticalConnector, { type }),
    ),
    createElement(
      'div',
      { key: 'node', className: 'tw-absolute tw-left-0 tw-top-[192px] tw-flex' },
      createElement(PathEndNode, { type }),
    ),
    createElement(
      'div',
      { key: 'milestone', className: 'tw-absolute tw-left-1/2 tw-top-[24px] tw-z-10 tw-translate-x-[-50%]' },
      createElement(TargetMilestoneBadge, { type, label: unitPathSegmentLabel(ordinal) }),
    ),
  );
};

// The full column of target paths: each path rendered top to bottom, consecutive paths joined by an
// inter-path connector carrying a numbered badge. All states come from the shared progression.
const renderPaths = (
  consolidated: StudentSkillStates[][][],
  state: UnitPathState,
  cursor: UnitPathCursor,
  practicingSkillIds: Set<string>,
  // True when there is no foundations bridge, so the first path must carry the "Path to target 1"
  // segment anchor itself (otherwise the bridge already carries it).
  stampFirstPathAnchor: boolean,
): ReactElement | null => {
  if (consolidated.length === 0) {
    return null;
  }

  const elements: ReactElement[] = [];
  consolidated.forEach((levels, pathIndex) => {
    if (pathIndex > 0) {
      const above = consolidated[pathIndex - 1];
      // Badge numbering continues after the foundations→first-path bridge (index 1), so the connector
      // leading into path `pathIndex` is badge `pathIndex + 1`.
      const connectorIndex = cursor.connector++;
      const type = state.connectorType(connectorIndex);
      const pathSkills = levels.flat();
      elements.push(
        renderConnector({
          key: `path-bridge-${pathIndex + 1}`,
          fromRowSize: lastRowSize(above),
          toRowSize: firstRowSize(levels),
          type,
          variant: 'path',
          badgeIndex: pathIndex + 1,
          span: 'target',
          milestone: createElement(TargetMilestoneBadge, {
            type: targetMilestoneType(above, type),
            label: unitPathSegmentLabel(pathIndex),
          }),
          wrapperId: anchorIdFor(connectorIndex, state),
          segmentKey: unitPathSegmentKey(pathIndex + 1),
          segmentLabel: unitPathSegmentLabel(pathIndex + 1),
          segmentCompleted: passedCount(pathSkills),
          segmentTotal: pathSkills.length,
          segmentName: targetSkillName(pathSkills),
        }),
      );
    }

    const firstPathAnchor = stampFirstPathAnchor && pathIndex === 0;
    const path = renderPath(
      levels,
      pathIndex,
      state,
      cursor,
      practicingSkillIds,
      firstPathAnchor ? unitPathSegmentKey(1) : undefined,
      firstPathAnchor ? unitPathSegmentLabel(1) : undefined,
    );
    if (path) {
      elements.push(path);
    }
  });
  elements.push(renderPathEnd(consolidated[consolidated.length - 1], consolidated.length));

  // No top margin: the bridge above carries its full span as layout height, so the
  // first path card's top border already lands exactly where the connector ends.
  return createElement('div', { className: 'tw-flex tw-flex-col tw-items-center' }, elements);
};

// The bridge joining the foundations panel to the first target path. Returns null when either side
// has no row to connect.
const renderBridge = (
  state: UnitPathState,
  cursor: UnitPathCursor,
  fromRowSize: number,
  toRowSize: number | null,
  // Progress + target name of the first path (the "Target 1" segment this bridge introduces).
  firstPathCompleted: number,
  firstPathTotal: number,
  firstPathName: string,
): ReactElement | null => {
  if (!toRowSize || fromRowSize < 1) {
    return null;
  }

  const connectorIndex = cursor.connector++;
  const type = state.connectorType(connectorIndex);
  return renderConnector({
    key: 'bridge-connector',
    fromRowSize,
    toRowSize,
    type,
    variant: 'path',
    badgeIndex: 1,
    span: 'foundations',
    milestone: createElement(FoundationsMilestoneBadge, { type }),
    wrapperId: anchorIdFor(connectorIndex, state),
    segmentKey: unitPathSegmentKey(1),
    segmentLabel: unitPathSegmentLabel(1),
    segmentCompleted: firstPathCompleted,
    segmentTotal: firstPathTotal,
    segmentName: firstPathName,
  });
};

/**
 * Builds the whole student unit path — the foundations panel, the foundations→first-path bridge, and
 * the connected target paths — as a single memoized element. Foundations and paths behave as ONE
 * continuous path: connector states (passed / unlocked / locked) flow from the first foundation to the
 * last path skill via computeUnitPathState. Reads the student skills, optimistic take-assessment state,
 * and the active unit's colors from Redux; callers pass the raw paths breakdown and foundation skills.
 */
export const useDashboardUnitPath = ({
  paths,
  foundationSkills,
  lessonPractice,
}: UseDashboardUnitPathParams): UseDashboardUnitPathResult => {
  const studentSkills = useAppSelector(selectedStudentSkillStates);
  const optimistic = useAppSelector(selectedStudentSkillStateAfterTakeAssessment);
  const unit = useAppSelector(selectedUnit);

  return useMemo(() => {
    const studentSkillStates = applyOptimisticValues(studentSkills ?? [], optimistic.nextState);
    const unitColors = unitFoundationsCardColors[resolveUnitBaseColor(unit?.name)];

    // Skill ids (in the "SkillNode:<n>" global-id form shared by both sources) the student is
    // currently practising, used to flag matching cards via DashboardCard's `isPracticing` prop.
    const practicingSkillIds = new Set(lessonPractice.flatMap(session => (session.skill ? [session.skill.id] : [])));

    const orderedFoundations = sortFoundationsSkills(foundationSkills);
    const foundationCards = skillSelector(orderedFoundations, studentSkillStates);
    const consolidated = paths ? consolidatePaths(paths, studentSkillStates, foundationSkills) : [];
    const foundationsComplete = areFoundationsAllPassed(foundationSkills, studentSkillStates);

    const state = computeUnitPathState(foundationCards, consolidated, foundationsComplete);
    const cursor: UnitPathCursor = { card: 0, connector: 0 };

    const foundationsPanel = renderFoundations(foundationCards, unitColors, state, cursor, practicingSkillIds);

    if (consolidated.length === 0) {
      return {
        unitPath: foundationsPanel,
        positionKey: state.positionConnectorIndex,
        hasUnlockedFrontier: state.firstUnlockedConnectorIndex !== null,
        hasPassedConnector: state.hasPassedConnector,
      };
    }

    const firstPathSkills = consolidated[0].flat();
    const bridge = renderBridge(
      state,
      cursor,
      foundationsLastRowSize(foundationCards.length),
      firstRowSize(consolidated[0]),
      passedCount(firstPathSkills),
      firstPathSkills.length,
      targetSkillName(firstPathSkills),
    );
    // With no bridge (e.g. no foundations), the first path carries the "Path to target 1" anchor itself.
    const pathsColumn = renderPaths(consolidated, state, cursor, practicingSkillIds, bridge === null);

    return {
      unitPath: (
        <>
          {foundationsPanel}
          {bridge}
          {pathsColumn}
        </>
      ),
      positionKey: state.positionConnectorIndex,
      hasUnlockedFrontier: state.firstUnlockedConnectorIndex !== null,
      hasPassedConnector: state.hasPassedConnector,
    };
  }, [paths, foundationSkills, lessonPractice, studentSkills, optimistic.nextState, unit]);
};
